import {
	ProviderType,
	ProviderTypeToMonetizationTypes,
	ProviderTypeWithMonetizationTypes,
} from '@/components/filter-bar/filter-bar.types';
import { Genre, Subgenre } from '@/constants/types';
import { GetConstantsDocument } from '@/graphql/queries/GetConstants.query';
import { pluck } from '@/helpers/object-helper';
import { headerfulCachedRequest } from '@/helpers/request-helper';
import Provider from '@/interfaces/provider';

import { GetSubgenresQueryVariables, GetSubgenresQuery } from '@/pages/graphql/queries/GetSubgenres.query';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { activeSubgenreCountriesWebLocales } from '@/constants/subgenres';

interface ConstantsResponse {
	genres: Genre[];
	movies: Array<{ __typename?: 'AgeCertification'; technicalName: string; id: number }>;
	organizations: string[];
	shows: Array<{ __typename?: 'AgeCertification'; technicalName: string; id: number }>;
}

export const useConstantStore = defineStore('constant', () => {
	const userStore = useUserStore();
	const routingStore = useRoutingStore();
	const languageStore = useLanguageStore();

	const providersState = ref<Provider[]>([]);
	const upcomingProviders = ref<number[]>([]);
	const subgenresList = ref<Subgenre[]>([]);
	const genres = ref<Genre[]>([]);
	const ageCertifications = ref<
		Pick<ConstantsResponse, 'movies' | 'shows' | 'organizations'> & { hasCertifications: boolean }
	>({
		hasCertifications: false,
		movies: [],
		shows: [],
		organizations: [],
	});

	const allProvidersById = computed(() => {
		return pluck(allProviders(false), 'packageId');
	});

	const allProvidersByShortName = computed(() => {
		return pluck(allProviders(false), 'shortName');
	});

	const sportProviders = computed(() => {
		return providersState.value.filter(p => p.hasSport);
	});

	const titleProviders = computed(() => {
		return providersState.value.filter(p => p.hasTitles);
	});

	const titleProviderCount = computed(() => {
		return titleProviders.value.length;
	});

	const sportProvidersById = computed(() => {
		return pluck(sportProviders.value, 'packageId');
	});

	const sportProvidersByShortName = computed(() => {
		return pluck(sportProviders.value, 'shortName');
	});

	const titleProvidersById = computed(() => {
		return pluck(titleProviders.value, 'packageId');
	});

	const titleProvidersByShortName = computed(() => {
		return pluck(titleProviders.value, 'shortName');
	});

	const providersById = computed(() => {
		if (!routingStore.activeRoute?.name?.startsWith('app.sports')) {
			return titleProvidersById.value;
		}
		return sportProvidersById.value;
	});

	const providersByShortName = computed(() => {
		if (!routingStore.activeRoute?.name?.startsWith('app.sports')) {
			return titleProvidersByShortName.value;
		}
		return sportProvidersByShortName.value;
	});

	const providersByType = computed(() => {
		return providers(false).reduce((providersByType: Record<ProviderType, Provider[]>, provider: Provider) => {
			const providerType = Object.keys(ProviderTypeToMonetizationTypes).find(providerType =>
				provider.monetizationTypes?.find(mt =>
					ProviderTypeToMonetizationTypes[providerType as ProviderTypeWithMonetizationTypes].includes(mt)
				)
			) as ProviderType;

			if (!providerType) {
				return providersByType;
			}

			return {
				...providersByType,
				[providerType]: [...(providersByType[providerType] || []), provider],
			};
		}, {} as Record<ProviderType, Provider[]>);
	});

	const genresByShortName = computed(() => {
		return pluck(genres.value, 'shortName');
	});

	/**
	 * get first three providers of the region to preselect for a new user
	 */
	const preselectedProviders = computed(() => {
		const { providers: userProviders = [] } = userStore.settings || {};
		return userProviders.length > 0
			? userProviders
			: (providers(false) as Provider[]).slice(0, 3).map(provider => provider.shortName);
	});

	/** Visible providers for the provider filter bar. */
	function allProviders(filterByUserProviders = true) {
		let filteredProviders = providersState.value || [];
		let userProviders = userStore.settingsProvider;
		// cheap trick for the wrong serialization userProviders on the server is an object instad of an array
		// loggedInProviders is also affected
		if (!(userProviders instanceof Array)) {
			userProviders = Object.values(userProviders);
		}

		if (filterByUserProviders && userProviders.length) {
			filteredProviders = filteredProviders.filter(provider => userProviders.includes(provider.shortName));
		}
		return filteredProviders;
	}

	function providers(filterByUserProviders = true) {
		if (!routingStore.activeRoute?.name?.startsWith('app.sports')) {
			return allProviders(filterByUserProviders).filter((p: Provider) => p.hasTitles);
		}

		return sportProviders.value;
	}

	async function fetchPackages() {
		const query = `query GetPackages($country: Country!, $platform: Platform! = WEB) {
			packages(country: $country, platform: $platform, includeAddons: true) {
				cn: clearName
				hs: hasSport(country: $country, platform: $platform)
				ht: hasTitles(country: $country, platform: $platform)
				i: icon
				mt: monetizationTypes
				p: packageId
				sn: shortName
				s: slug
				tn: technicalName
				ivfb: isVisibleFilterBar(country: $country, platform: $platform)
			}
		}`;

		const variables = {
			country: languageStore.country,
		};

		const jwId = userStore.jwId;
		const response = await headerfulCachedRequest(query, variables, jwId as string);
		const { packages = [] } = response?.data;

		// Google has a crawl limit where they stop crawling as soon as the page size, JS asset or CSS asset goes beyond 2mb (ungzipped).
		// we dump our graphql and states onto HTML, that's why it increases in size.
		// since we request GetPackages on *every* page, it's worth it to make the manual optimisation.
		// note: in a perfect world, we wouldn't hardcode Provider, but use it from a query.
		const shortenedKeys: Record<string, keyof Provider> = {
			cn: 'clearName',
			hs: 'hasSport',
			ht: 'hasTitles',
			i: 'icon',
			mt: 'monetizationTypes',
			p: 'packageId',
			sn: 'shortName',
			s: 'slug',
			tn: 'technicalName',
			ivfb: 'isVisibleFilterBar',
		};

		const packagesOverride = (packages as any[])
			// TEMP CHANGE: Override 'amazon-prime' slug to 'amazon-prime-video' until we fix it this one on the content side
			.map(el => {
				if (el.slug === 'amazon-prime') {
					return {
						...el,
						slug: 'amazon-prime-video',
					};
				}
				return el;
			})
			// rename the shortened keys
			.map(el => {
				return Object.keys(el).reduce((root, key) => {
					const item = shortenedKeys[key];
					root[item] = el[key];
					return root;
				}, {} as Record<string, string>);
			});

		const items = [...packagesOverride].map(item => Object.freeze(item)) as unknown as Provider[];
		providersState.value = items;
	}

	async function fetchSubgenres() {
		//objectType = movie | show returns same data, only order is changed
		const query = `query GetSubgenres($country: Country!, $language: Language!) {
			subgenres(objectType: "movie", country: $country) {
				id
				content(country: $country, language: $language) {
					name
					fullPath
					shortName
					moviesUrl {
						fullPath
					}
					showsUrl {
						fullPath
					}
				}
			}
		}
		`;
		const variables: GetSubgenresQueryVariables = {
			country: languageStore.country,
			language: languageStore.language,
		};
		const jwId = userStore.jwId;
		const response = await headerfulCachedRequest(query, variables, jwId as string);
		const { subgenres = [] } = response?.data;
		const subs: Subgenre[] = (subgenres as GetSubgenresQuery['subgenres']).map(s => {
			const content: Subgenre = s.content;
			content.id = s.id;

			if (content.moviesUrl) {
				content.slug = content.moviesUrl.fullPath.split('/').at(-2);
			} else if (content.showsUrl) {
				content.slug = content.showsUrl.fullPath.split('/').at(-2);
			}
			return content;
		});

		subgenresList.value = subs;
	}

	async function fetchConstants() {
		const variables: GetSubgenresQueryVariables = {
			country: languageStore.country,
			language: languageStore.language,
		};
		const jwId = userStore.jwId;
		const response = await headerfulCachedRequest(GetConstantsDocument, variables, jwId as string);
		const { genres: genresResponse, movies, organizations, shows } = response?.data as ConstantsResponse;

		const constantsFromApi = {
			genres: genresResponse,
			movies,
			organizations,
			shows,
		};

		genres.value = constantsFromApi.genres;
		ageCertifications.value = {
			hasCertifications: constantsFromApi.movies.length > 0 || constantsFromApi.shows.length > 0,
			movies: constantsFromApi.movies,
			shows: constantsFromApi.shows,
			organizations: [
				...new Set(
					constantsFromApi.organizations
						.map((item: any) => item.organization)
						.filter((name: string) => name !== '')
				),
			],
		};
	}

	function setUpcomingProviders(providerIds: number[]) {
		upcomingProviders.value = providerIds;
	}

	watch(
		() => languageStore.webLocale,
		async (webLocale, oldWebLocale) => {
			if (webLocale === oldWebLocale) return;

			fetchPackages();
			fetchConstants();

			// refetch subgenre translations and fullPaths info
			if (activeSubgenreCountriesWebLocales.includes(webLocale)) {
				fetchSubgenres();
			}

			// Update Sentry tag when country changes
			if (process.client) {
				const { updateSentryTags } = await import('@/helpers/sentry-helper');
				updateSentryTags({ store_country: languageStore.country });
			}
		},
		{ immediate: false }
	);

	return {
		providersState,
		upcomingProviders,
		subgenresList,
		genres,
		ageCertifications,

		allProvidersById,
		allProvidersByShortName,
		sportProviders,
		titleProviders,
		titleProviderCount,
		sportProvidersById,
		sportProvidersByShortName,
		titleProvidersById,
		titleProvidersByShortName,
		providersById,
		providersByShortName,
		providersByType,
		genresByShortName,
		preselectedProviders,

		allProviders,
		providers,
		fetchPackages,
		fetchSubgenres,
		fetchConstants,
		setUpcomingProviders,
	};
});
